import React, { type FunctionComponent, useState } from 'react'
import { colors, Label, LoadingDots, Modal, spacing } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import moment from 'moment/moment'
import { IssueIcon, PhoneIcon, RefreshIcon } from '@instacart/ids-core'
import { useDashMessages } from 'intl/intl.hooks'
import { type PartnerPickRecurringShiftConflictCheckQuery } from 'domains/partner-pick-devices/api'
import FormattedTimeWithOffset from 'legacy/components/FormattedTimeWithOffset'
import { FormHeader } from 'domains/shifts/components/recurring-shifts/FormHeader'
import { ConfirmationModalType } from 'domains/shifts/components/recurring-shifts/PartnerPickRecurringShiftsForm'
import { getRecurringShiftDescription } from 'domains/shifts/components/recurring-shifts/partnerPickRecurringShiftForm.utils'
import {
  StyledCancelButton,
  StyledDeleteButton,
  StyledErrorMessage,
  StyledSubmitButton,
} from 'domains/shifts/components/recurring-shifts/ShiftDetailsForm.styles'
import { RecurringShiftsRadioGroup } from './RecurringShiftsRadioGroup'
import { RecurringShiftsRadio } from './RecurringShiftsRadio'

interface Props {
  currentShiftDescription?: React.ReactNode
  modalType: ConfirmationModalType
  isOpen: boolean
  onConfirm: (data: any) => void
  onCancel: () => void
  shopperId: number
  singleShiftConflicts: PartnerPickRecurringShiftConflictCheckQuery['partnerPickRecurringShiftConflictCheck']['singleShiftConflicts']
  recurringShiftConflicts: PartnerPickRecurringShiftConflictCheckQuery['partnerPickRecurringShiftConflictCheck']['recurringShiftConflicts']
  date: string
  isEditMode: boolean
  conflictsLoading: boolean
  confirmLoading: boolean
  getLocalShopperDetails: (shopperId: number) => object
  zoneOffset: number
  errorMessage: string
}

export enum RadioOptions {
  onlyThis = 'ONLY_THIS',
  thisAndFollowing = 'THIS_AND_FOLLOWING',
}

const StyledContainer = styled.div({
  padding: spacing.X24,
})

const StyledFooter = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
})

const StyledContent = styled.div({
  padding: spacing.X4,
  fontWeight: 'normal',
  fontSize: '14px',
})

export const StyledLabel = styled(Label)({
  color: colors.GRAYSCALE.X50,
  fontSize: '15px',
  paddingRight: '10px',
  marginBottom: spacing.X4,
  fontWeight: 600,
})

const StyledShiftName = styled.div({
  padding: spacing.X4,
  marginLeft: '25px',
})

const StyledIconRow = styled.div({
  padding: spacing.X4,
  display: 'flex',
  alignItems: 'center',
})

const StyledMessage = styled.div({
  marginTop: '10px',
  marginLeft: '10px',
})

const StyledConflictsSection = styled.div({
  maxHeight: '110px',
  overflowY: 'scroll',
  overflowX: 'hidden',
})

const StyledLoadingSection = styled.div({
  minHeight: '320px',
  overflowY: 'scroll',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
})

const StyledDescription = styled.div({
  marginLeft: '5px',
  whiteSpace: 'pre',
})

const StyledShiftTitle = styled.div({
  display: 'inline',
  '> strong': {
    fontWeight: 600,
  },
})

const StyledShiftDescription = styled.div({
  display: 'inline',
})

export const ConfirmationModal: FunctionComponent<Props> = ({
  date,
  currentShiftDescription,
  modalType,
  isOpen,
  onConfirm,
  onCancel,
  shopperId,
  singleShiftConflicts,
  recurringShiftConflicts,
  isEditMode,
  getLocalShopperDetails,
  conflictsLoading,
  confirmLoading,
  zoneOffset,
  errorMessage,
}) => {
  const messages = useDashMessages(
    {
      cancel: 'shiftsDomain.confirmation.modal.cancel',
      confirm: 'shiftsDomain.confirmation.modal.confirm',
      saveChanges: 'shiftsDomain.confirmation.modal.save.changes',
      deleteLabel: 'shiftsDomain.confirmation.modal.delete',
      deleteTitle: 'shiftsDomain.confirmation.modal.delete.title',
      saveChangesTitle: 'shiftsDomain.confirmation.modal.save.title',
      repeatShift: 'shiftsDomain.prompt.repeatShift',
      saveChangesToRecurringShift: 'shiftsDomain.confirmation.modal.save.title',
      checking: 'shiftsDomain.confirmation.modal.checking',
      conflicts: 'shiftsDomain.confirmation.modal.conflicts.overwrite',
    },
    {
      numConflicts: singleShiftConflicts?.length + recurringShiftConflicts?.length,
    }
  )

  const [deleteRadioValue, setDeleteRadioValue] = useState<RadioOptions>(RadioOptions.onlyThis)
  const [saveRadioValue, setSaveRadioValue] = useState<RadioOptions>(RadioOptions.onlyThis)
  const [editRadioValue, setEditRadioValue] = useState<RadioOptions>(RadioOptions.onlyThis)

  //Pass the selected option to parent onConfirm where appropriate APIs are called.
  const handleOnConfirm = e => {
    let confirmData = e
    if (modalType === ConfirmationModalType.Delete) {
      confirmData = deleteRadioValue
    }
    if (modalType === ConfirmationModalType.Regular) {
      confirmData = saveRadioValue
    }
    if (modalType === ConfirmationModalType.Edit) {
      confirmData = editRadioValue
    }
    if (modalType === ConfirmationModalType.Conflicts) {
      // form is submitted (create or edit shift) in parent
    }
    onConfirm(confirmData)
  }

  const getTitle = () => {
    switch (modalType) {
      case ConfirmationModalType.Delete:
        return messages.deleteTitle

      case ConfirmationModalType.Regular:
        return messages.saveChangesTitle

      case ConfirmationModalType.Edit:
        return messages.saveChangesTitle

      case ConfirmationModalType.Conflicts:
        return isEditMode ? messages.saveChangesToRecurringShift : messages.repeatShift
    }
  }

  const Header = () => <FormHeader onCancel={onCancel} title={getTitle()} />
  const shopper = getLocalShopperDetails(shopperId)
  // @ts-ignore
  const shopperName = `${shopper?.first_name} ${shopper?.last_name}`

  const numConflicts = singleShiftConflicts?.length + recurringShiftConflicts?.length || 0
  const hasConflicts = numConflicts > 0

  const RecurringShiftDescription = props => {
    const { shift } = props
    if (!shift) {
      return
    }

    const { timespan, description } = getRecurringShiftDescription({
      shiftStartSecondsSinceMidnight: shift.shiftStartSecondsSinceMidnight,
      shiftLengthSeconds: shift.shiftLengthSeconds,
      daysOfWeek: shift.daysOfWeek,
      date,
    })

    // converting 'Every' to 'every' because we have 'Repeats' in front of it
    const lowercasedDescription =
      description.split(' ').length >= 2
        ? description
            .split(' ')
            .map((word, index) => (index === 0 ? word.toLowerCase() : word))
            .join(' ')
        : description

    return (
      <StyledShiftName>
        <StyledShiftDescription>Repeats {lowercasedDescription}</StyledShiftDescription>
        {', '}
        <StyledShiftTitle>
          {timespan} <strong>(recurring)</strong>
        </StyledShiftTitle>
      </StyledShiftName>
    )
  }

  const SingleShiftDescription = props => {
    const { shift } = props
    if (!shift) {
      return
    }

    return (
      <StyledShiftName>
        <FormattedTimeWithOffset
          value={moment(shift.startsAt).toDate()}
          offset={zoneOffset}
          format={'ddd, D MMM, h:mm a'} // e.g. "Thu, 24 Jun, 9:30pm - 11:30pm"
        />
        <FormattedTimeWithOffset
          value={moment(shift.endsAt).toDate()}
          offset={zoneOffset}
          format={'-h:mm a'}
        />
      </StyledShiftName>
    )
  }

  const Content = () => {
    switch (modalType) {
      case ConfirmationModalType.Conflicts:
        return (
          <StyledContent data-testid="shopper-shifts-confirmation-modal-conflict-content">
            <StyledIconRow>
              <PhoneIcon color="systemGrayscale70" />
              <StyledDescription>{shopperName}</StyledDescription>
            </StyledIconRow>

            <StyledIconRow style={{ marginBottom: '20px', display: 'flex' }}>
              <RefreshIcon
                color="systemGrayscale70"
                style={{ alignSelf: 'flex-start', marginTop: '4px' }}
              />
              <StyledDescription>{currentShiftDescription}</StyledDescription>
            </StyledIconRow>

            {hasConflicts && (
              <>
                <StyledIconRow>
                  <IssueIcon color={'systemDetrimentalRegular'} size="19px" />
                  <StyledDescription>{`${numConflicts} ${messages.conflicts}`}</StyledDescription>
                </StyledIconRow>
                <StyledConflictsSection>
                  {singleShiftConflicts?.map(s => (
                    <SingleShiftDescription shift={s} key={s.id} />
                  ))}
                  {recurringShiftConflicts?.map(r => (
                    <RecurringShiftDescription shift={r} key={r.id} />
                  ))}
                </StyledConflictsSection>
              </>
            )}
          </StyledContent>
        )
      case ConfirmationModalType.Delete:
        return (
          <StyledContent>
            <RecurringShiftsRadioGroup>
              <RecurringShiftsRadio
                labelMessageId={'shiftsDomain.confirmation.modal.delete.only.this'}
                selectedValue={deleteRadioValue}
                identifier={'deleteOption'}
                value={RadioOptions.onlyThis}
                onChange={setDeleteRadioValue}
              />
              <RecurringShiftsRadio
                labelMessageId={'shiftsDomain.confirmation.modal.delete.this.and.following'}
                selectedValue={deleteRadioValue}
                identifier={'deleteOption'}
                value={RadioOptions.thisAndFollowing}
                onChange={setDeleteRadioValue}
              />
            </RecurringShiftsRadioGroup>
          </StyledContent>
        )
      case ConfirmationModalType.Edit:
        return (
          <StyledContent data-testid="shopper-shifts-confirmation-modal-edit-content">
            <RecurringShiftsRadioGroup>
              <RecurringShiftsRadio
                labelMessageId={'shiftsDomain.confirmation.modal.save.only.this'}
                selectedValue={editRadioValue}
                identifier={'editOption'}
                value={RadioOptions.onlyThis}
                onChange={setEditRadioValue}
              />
              <RecurringShiftsRadio
                labelMessageId={'shiftsDomain.confirmation.modal.save.this.and.following'}
                selectedValue={editRadioValue}
                identifier={'editOption'}
                value={RadioOptions.thisAndFollowing}
                onChange={setEditRadioValue}
              />
            </RecurringShiftsRadioGroup>
          </StyledContent>
        )
      case ConfirmationModalType.Regular:
        return (
          <StyledContent>
            <RecurringShiftsRadioGroup>
              <RecurringShiftsRadio
                labelMessageId={'shiftsDomain.confirmation.modal.save.only.this'}
                selectedValue={saveRadioValue}
                identifier={'saveOption'}
                value={RadioOptions.onlyThis}
                onChange={setSaveRadioValue}
              />
              <RecurringShiftsRadio
                labelMessageId={'shiftsDomain.confirmation.modal.save.this.and.following'}
                selectedValue={saveRadioValue}
                identifier={'saveOption'}
                value={RadioOptions.thisAndFollowing}
                onChange={setSaveRadioValue}
              />
            </RecurringShiftsRadioGroup>
          </StyledContent>
        )
    }
  }

  const Footer = () => {
    switch (modalType) {
      case ConfirmationModalType.Conflicts:
        return (
          <StyledFooter>
            <StyledCancelButton
              data-testid="shift-details-form-cancel"
              onClick={onCancel}
              variant="secondary"
              type="button"
            >
              {messages.cancel}
            </StyledCancelButton>
            <StyledSubmitButton
              data-testid="shopper-shifts-confirmation-modal-submit"
              type="submit"
              onClick={handleOnConfirm}
              disabled={confirmLoading}
            >
              {confirmLoading ? <LoadingDots height={10} /> : messages.confirm}
            </StyledSubmitButton>
          </StyledFooter>
        )
      case ConfirmationModalType.Delete:
        return (
          <StyledFooter>
            <StyledCancelButton onClick={onCancel} variant="secondary" type="button">
              {messages.cancel}
            </StyledCancelButton>
            <StyledDeleteButton
              data-testid="shopper-shifts-confirmation-modal-delete-btn"
              onClick={handleOnConfirm}
              variant="detrimental"
              type="button"
              disabled={confirmLoading}
            >
              {confirmLoading ? <LoadingDots height={10} /> : messages.deleteLabel}
            </StyledDeleteButton>
          </StyledFooter>
        )
      case ConfirmationModalType.Regular:
        return (
          <StyledFooter>
            <StyledCancelButton onClick={onCancel} variant="secondary" type="button">
              {messages.cancel}
            </StyledCancelButton>
            <StyledSubmitButton
              onClick={handleOnConfirm}
              variant="secondary"
              type="button"
              disabled={confirmLoading}
            >
              {confirmLoading ? <LoadingDots height={10} /> : messages.confirm}
            </StyledSubmitButton>
          </StyledFooter>
        )
      case ConfirmationModalType.Edit:
        return (
          <StyledFooter>
            <StyledCancelButton onClick={onCancel} variant="secondary" type="button">
              {messages.cancel}
            </StyledCancelButton>
            <StyledSubmitButton
              onClick={handleOnConfirm}
              variant="primary"
              type="button"
              disabled={confirmLoading}
            >
              {confirmLoading ? <LoadingDots height={10} /> : messages.confirm}
            </StyledSubmitButton>
          </StyledFooter>
        )
    }
  }

  return (
    <Modal isOpen={isOpen} maxWidth={535} maxHeight={383} hideCloseButton>
      <StyledContainer data-testid="shopper-shifts-confirmation-modal">
        {conflictsLoading ? (
          <StyledLoadingSection>
            <LoadingDots height={12} />
            <StyledMessage> {messages.checking} </StyledMessage>
          </StyledLoadingSection>
        ) : (
          <>
            {Header()}
            {Content()}
            {errorMessage && <StyledErrorMessage> {errorMessage}</StyledErrorMessage>}
            {Footer()}
          </>
        )}
      </StyledContainer>
    </Modal>
  )
}
